import { Formik } from "formik";
import { LeaderboardBase } from "types";
import { useMemo } from "react";
import { LeaderboardFormValues } from "../LeaderboardEditable.types";
import { leaderboardDefaults } from "../LeaderboardEditable.config";
import useGroups from "providers/GroupsProvider/useGroups";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";
import useExercises from "providers/ExercisesProvider/useExercises";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import useGetAllCircuitsQuery from "hooks/useGetAllCircuitsQuery";
import { circuitsEnabled } from "config/features";
import useCanDo from "hooks/useCanDo";

interface LeaderboardFormikProviderProps {
  leaderboard?: LeaderboardBase;
  onSubmit(leaderboard: LeaderboardBase): void;
  children: React.ReactNode;
}

function LeaderboardFormikProvider(props: LeaderboardFormikProviderProps) {
  const { onSubmit, children, leaderboard = leaderboardDefaults } = props;
  const exercises = useExercises();
  const scores = useHyroxMetadata();
  const groups = useGroups();
  const organisation = useOrganisation();
  const canAccessCircuits = useCanDo("library.any");
  const { data: circuits } = useGetAllCircuitsQuery({ enabled: circuitsEnabled && canAccessCircuits });

  const initialValues: LeaderboardFormValues = useMemo(() => {
    return LeaderboardHelpers.toForm(leaderboard, { groups, exercises, scores, circuits });
  }, [exercises, scores, circuits, groups, leaderboard]);

  async function handleSubmit(submittedLeaderboard: LeaderboardFormValues) {
    const updatedLeaderboard = LeaderboardHelpers.fromForm(submittedLeaderboard);

    return onSubmit({
      ...updatedLeaderboard,
      groupIds: organisation.featureConfig.isGroupsEnabled ? updatedLeaderboard.groupIds : leaderboard.groupIds,
    });
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {children}
    </Formik>
  );
}

export default LeaderboardFormikProvider;
